// The public privacy policy and terms of service, as plain data.
//
// DRAFT, NOT LEGAL ADVICE. The text was written from what the code actually does (data stored,
// third parties, cookies, retention, billing) and needs Ross's review, ideally a solicitor's,
// before it is published. Until then:
//   - LEGAL_PAGES_READY stays false, so App.jsx mounts no /privacy or /terms route and the
//     footer shows no Legal column;
//   - the company details below stay null, and the pages render them as "[company name]" etc.
// legalContent.test.js refuses LEGAL_PAGES_READY = true while any company detail is missing.
//
// Tokens in the text: {product} is the brand's product name (a partner's on its own host);
// {entity}, {address}, {companyNumber}, {icoNumber}, {privacyEmail}, {supportEmail},
// {hostingLocation} and {governingLaw} come from LEGAL_ENTITY.
//
// Kept free of JSX and imports so `node --test` can load it.

export const LEGAL_PAGES_READY = false

export const LEGAL_ENTITY = {
    entity: null, // registered company name, e.g. "Example Energy Ltd"
    address: null, // registered office address
    companyNumber: null, // Companies House number
    icoNumber: null, // ICO data protection fee registration number
    privacyEmail: null, // inbox for data protection requests
    supportEmail: null, // inbox for account and billing questions
    hostingLocation: null, // where the servers and backups are, e.g. "the European Union"
    governingLaw: null // e.g. "Scotland" or "England and Wales"
}

const PLACEHOLDERS = {
    entity: '[company name]',
    address: '[registered address]',
    companyNumber: '[company number]',
    icoNumber: '[ICO registration number]',
    privacyEmail: '[privacy email]',
    supportEmail: '[support email]',
    hostingLocation: '[hosting location]',
    governingLaw: '[governing law]'
}

/** The LEGAL_ENTITY keys still unset. Publishing is blocked until this is empty. */
export function missingLegalDetails(entity = LEGAL_ENTITY) {
    return Object.keys(PLACEHOLDERS).filter((key) => !entity[key])
}

/** Replace the {tokens} in one string of legal text. */
export function fillLegalText(
    text,
    { productName = 'Power', entity = LEGAL_ENTITY } = {}
) {
    return text.replace(/\{(\w+)\}/g, (match, key) => {
        if (key === 'product') return productName
        if (key in PLACEHOLDERS) return entity[key] || PLACEHOLDERS[key]
        return match
    })
}

export const PRIVACY_POLICY = {
    path: '/privacy',
    title: 'Privacy policy',
    lastUpdated: '[date of publication]',
    summary:
        '{product} is run by {entity}. This policy explains what personal data the service holds, why, who it is shared with, how long it is kept, and the rights you have over it.',
    sections: [
        {
            heading: 'Who we are, and who is in charge of your data',
            body: [
                '{entity} (company number {companyNumber}, registered office {address}) provides {product}. We are registered with the Information Commissioner’s Office under number {icoNumber}.',
                'When an organisation (a school, trust, landlord, business or partner) uses {product}, that organisation decides whose data goes in and why. It is the controller of its staff’s, pupils’ and tenants’ personal data, and we process that data on its behalf under our agreement with it. If you use {product} through your school or employer, ask them first about your data; we will help them answer.',
                'We are the controller for the data we collect for ourselves: enquiries through our contact form, people who sign up for an account themselves, and our own sales and billing records.'
            ]
        },
        {
            heading: 'What we hold',
            body: [
                {
                    list: [
                        'Account details: your name, email address, role and the organisation you belong to. Optionally a phone number, profile photo, location, time zone and short bio, if you or your organisation add them.',
                        'Sign-in data: if you sign in with Microsoft, Google or Facebook, the identifiers and tokens that provider returns. Your password, if you have one, is stored only as a one-way hash. For each signed-in session we keep the time of use, and normally the IP address and browser type.',
                        'School records imported from the school’s management information system (iSAMS): names, preferred names, school email, mobile number, system identifiers and, for pupils, their boarding house. We do not import year groups, dates of birth or any special category data.',
                        'Energy data: half-hourly and daily meter readings, meter and supply numbers, tariffs and contracts, and the costs and carbon worked out from them. This describes buildings, not people, except where a single home is monitored.',
                        'What you do in the service: alerts you set or acknowledge, notes and evidence you record, quiz scores, messages you send to colleagues in the service, notifications, and an audit trail of changes made to accounts and settings.',
                        'Billing: the name of the paying organisation, its billing email and its subscription status. Card details are entered on Stripe’s pages and never reach us.',
                        'Enquiries: what you send through our contact form (name, email, organisation, role and message). It is emailed to us and not stored in the service.'
                    ]
                }
            ]
        },
        {
            heading: 'Why we use it, and our lawful basis',
            body: [
                {
                    list: [
                        'To provide the service to your organisation: performing our contract with it, and, for data we process on its behalf, its instructions.',
                        'To sign you in and keep accounts secure, including spotting misuse: our legitimate interest in running a secure service.',
                        'To send the emails the service depends on, such as invitations, password resets, alerts and reports you subscribe to: performing the contract.',
                        'To bill subscriptions and keep accounting records: performing the contract and our legal obligations.',
                        'To answer an enquiry and follow it up: our legitimate interest in responding to people who contact us. We do not send marketing email to you unless you have asked for it.'
                    ]
                }
            ]
        },
        {
            heading: 'Pupils',
            body: [
                'Pupils’ accounts exist only where their school has connected its management information system. Pupils see energy data for their school and their own boarding house, and take part in quizzes and class projects. They cannot see costs or other people’s details.',
                'We do not use pupils’ data for anything other than providing the service to their school, and our usage monitoring records pupils’ activity without any identifier that could link it back to them.'
            ]
        },
        {
            heading: 'Who we share it with',
            body: [
                'We do not sell personal data. We use these service providers, each under a contract that limits them to acting on our instructions:',
                {
                    list: [
                        'Server hosting, in {hostingLocation}: the service and its database.',
                        'Cloudflare: storage for uploaded files and encrypted database backups, and the “are you human” check on our public forms.',
                        'Postmark (ActiveCampaign): sending the service’s emails.',
                        'Stripe: taking payments and managing subscriptions.',
                        'Microsoft, Google and Facebook: only if you or your organisation choose to sign in with them, or to send alert emails from your organisation’s own Microsoft account.',
                        'Anthropic: an assistant that answers questions about energy data. It is currently used only by our own staff. We will update this policy, and the relevant agreements, before it is offered to customers.'
                    ]
                },
                'To fetch your organisation’s meter readings we send its account and meter identifiers to the energy supplier or meter platform it has connected. To show local weather we send a site’s map coordinates, and nothing else, to a weather data service.',
                'Our monitoring tools (logs, metrics and error reports) run on our own servers, not with a third party.',
                'We may disclose data where the law requires it, or to protect the rights and safety of our users or others.'
            ]
        },
        {
            heading: 'International transfers',
            body: [
                'Our servers and backups are in {hostingLocation}. Some providers above (Stripe, Postmark, Microsoft, Google and Anthropic) may process data in the United States. Where they do, the transfer is covered by the UK’s adequacy regulations for the US data bridge or by the standard contractual clauses approved for UK transfers.'
            ]
        },
        {
            heading: 'How long we keep it',
            body: [
                {
                    list: [
                        'Account and school records: while your organisation uses {product}. When an organisation leaves, we delete its accounts and data on request, and in any case within [retention period after an organisation leaves].',
                        'Server logs and traces: 7 days. Usage metrics: 15 days. Records of our calls to energy providers: 90 days.',
                        'Assistant conversations: 90 days.',
                        'Database backups: daily backups for 14 days and monthly backups for about 13 months, so deleted data can remain in a backup for that long before it expires.',
                        'Invitations expire after 14 days; email confirmation links after 3 days.',
                        'Billing records: as long as tax law requires, normally six years.'
                    ]
                }
            ]
        },
        {
            heading: 'Cookies and browser storage',
            body: [
                '{product} uses only the cookies it needs to work: a session cookie that keeps you signed in, and a security cookie that protects forms from cross-site forgery. Both are first-party. We use no advertising or analytics cookies, so we do not ask for cookie consent.',
                'The service also saves a few preferences in your browser’s local storage, such as your chosen organisation, theme and whether the sidebar is collapsed. They never leave your device. The “are you human” check on our public forms is provided by Cloudflare Turnstile, which may set its own strictly necessary cookie.'
            ]
        },
        {
            heading: 'Your rights',
            body: [
                'You can ask for a copy of your personal data, have it corrected or deleted, restrict or object to how it is used, or have it given to you in a portable format. Where we rely on consent, you can withdraw it at any time.',
                'If your data is in {product} because of your school or employer, send your request to them; we will help them deal with it. Otherwise, email {privacyEmail}. We answer within one month.',
                'You can also complain to the Information Commissioner’s Office (ico.org.uk, 0303 123 1113). We would appreciate the chance to put things right first.'
            ]
        },
        {
            heading: 'Security',
            body: [
                'Connections to {product} are encrypted. Passwords are stored as one-way hashes, provider credentials are encrypted at rest, and backups are encrypted before they leave our servers. Access within an organisation follows the roles its administrators assign, and staff at {entity} access customer data only to provide and support the service.'
            ]
        },
        {
            heading: 'Changes to this policy',
            body: [
                'We will post any change here and update the date at the top. If a change materially affects how we use your data, we will tell organisation administrators by email before it takes effect.'
            ]
        }
    ]
}

export const TERMS_OF_SERVICE = {
    path: '/terms',
    title: 'Terms of service',
    lastUpdated: '[date of publication]',
    summary:
        'These terms are the agreement between {entity} and the organisation that subscribes to {product}. If you accept them on behalf of an organisation, you confirm you are authorised to do so.',
    sections: [
        {
            heading: 'The service',
            body: [
                '{product} collects energy readings from the meters, suppliers and platforms an organisation connects, and turns them into dashboards, alerts, reports and learning activities. The features available depend on the organisation’s plan, shown on our pricing page when you subscribe.',
                'We rely on third-party suppliers and meter platforms for readings. We cannot guarantee their data is complete or correct, and figures such as costs, carbon and savings are estimates based on it. Do not rely on them alone for billing disputes, statutory filings or safety decisions without checking the underlying readings.'
            ]
        },
        {
            heading: 'Accounts',
            body: [
                'Your organisation’s administrators decide who has an account and what each person can see. They are responsible for keeping that list current, including removing people who leave.',
                'Keep your sign-in details to yourself and tell us at {supportEmail} if you think someone else has used your account. Organisations that connect a school management information system are responsible for having a lawful basis to share their staff’s and pupils’ data with us.'
            ]
        },
        {
            heading: 'Free trial',
            body: [
                'A new organisation that signs up itself gets a 30-day free trial without giving card details. Its administrators are emailed before the trial ends. If no subscription is started, paid features switch off at the end of the trial; the organisation’s data is kept, so subscribing later picks up where it left off.'
            ]
        },
        {
            heading: 'Subscriptions and payment',
            body: [
                'Subscriptions are billed monthly in advance through Stripe, at the price shown when you subscribe, plus VAT where it applies. Moving to a different plan takes effect straight away and the charge is adjusted pro rata.',
                'If a payment fails, Stripe retries it and the service continues meanwhile. If payment is not made after those retries, the subscription ends and paid features switch off.',
                'We may change our prices. We will give administrators at least 30 days’ notice by email, and a change applies from the next billing period after that notice.'
            ]
        },
        {
            heading: 'Cancelling',
            body: [
                'Administrators can cancel at any time from the billing page. The subscription then runs to the end of the period already paid for, and is not renewed. [Refund policy: state whether any part-period refunds are given.]',
                'After cancellation your organisation’s data is kept for [period] so it can come back, then deleted on request or at the end of that period. Administrators can export their energy data before cancelling.'
            ]
        },
        {
            heading: 'Partners and resellers',
            body: [
                'If your organisation reached {product} through one of our partners, your contract and billing may be with that partner. Where it is, the partner’s terms govern your subscription, and these terms govern your use of the platform.'
            ]
        },
        {
            heading: 'Acceptable use',
            body: [
                'Do not use {product} to break the law, to upload material you have no right to share, to try to reach other organisations’ data, to probe or disrupt the service, or to resell access without an agreement with us. We may suspend an account that does any of these, and will tell its organisation’s administrators why.'
            ]
        },
        {
            heading: 'Your data',
            body: [
                'Your organisation owns its data. We use it to provide and support the service and as our privacy policy describes. We may use anonymised, aggregated figures that identify no organisation or person, for example to improve the service or to publish benchmarks; a named comparison with other organisations is only ever shown where an organisation has opted in.',
                'Where we process personal data on your organisation’s behalf, our data processing terms apply. [Link to, or attach, the data processing agreement.]'
            ]
        },
        {
            heading: 'Availability and changes',
            body: [
                'We aim to keep {product} available at all times but do not promise it will be uninterrupted. We may change or remove features. If a change takes away a significant feature you are paying for, we will tell administrators in advance, and you may cancel without charge for the remainder of the period.'
            ]
        },
        {
            heading: 'Liability',
            body: [
                'Nothing in these terms limits liability for death or personal injury caused by negligence, for fraud, or for anything else the law does not allow to be limited.',
                'Otherwise, neither side is liable for indirect or consequential loss, or for loss of profit, revenue or savings, and each side’s total liability under these terms in any 12 months is limited to the fees paid for the service in those 12 months.'
            ]
        },
        {
            heading: 'Ending the agreement',
            body: [
                'Either side may end the agreement if the other materially breaches it and does not put it right within 30 days of being told. We may also end it if the subscription is not paid. Sections on your data, liability and the law that applies continue after it ends.'
            ]
        },
        {
            heading: 'General',
            body: [
                'These terms are governed by the law of {governingLaw}, and its courts deal with any dispute. We may update them; we will post the new version here and tell administrators of any change that affects them before it applies. Questions: {supportEmail}.',
                '{entity}, registered in [country] under company number {companyNumber}. Registered office: {address}.'
            ]
        }
    ]
}
